import AutoResizeTextarea from '@/components/AutoResizeTextarea/AutoResizeTextarea';
import { InputProps } from '@/components/Input';
import { cleanCategoryForMutation } from '@/services/categories';
import { useUpdateCategoryMutation } from '@/services/categories/hooks';
import { IconCheckmarkSingle, IconClose, IconTrash } from '@/theme/icons';
import { Category } from '@/types/category';
import { refetchCategoryAndBlocks } from '@/utils/category';
import { getColorFromToken } from '@/utils/color';
import rem from '@/utils/rem';
import { Box, Flex, IconButton, Text, Textarea, VStack, useToast } from '@chakra-ui/react';
import { forwardRef, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { PURPOSE_SELECT_BEHAVIOR_BLOCK, PURPOSE_SELECT_BEHAVIOR_CATEGORY } from './constants';

export type PurposeSelectItem = { id: number; text: string; isUltimate?: boolean };

type PurposeSelectProps = InputProps & {
  behavior?: typeof PURPOSE_SELECT_BEHAVIOR_BLOCK | typeof PURPOSE_SELECT_BEHAVIOR_CATEGORY;
  category?: Category;
  hideLabel?: boolean;
  onChange: (value: string) => void;
  onUpdateCategory?: (updatedCategory: Category) => void;
};

type SelectItemProps = PurposeSelectItem & {
  category: PurposeSelectProps['category'];
  onUpdateCategory?: PurposeSelectProps['onUpdateCategory'];
  isMenuOpen: boolean;
  onSelect: () => void;
  onButtonInteraction: (interacting: boolean) => void;
};

const SelectItem = ({
  category,
  text,
  isUltimate,
  onUpdateCategory,
  isMenuOpen,
  onSelect,
  onButtonInteraction,
}: SelectItemProps) => {
  const { t } = useTranslation('PurposeSelect');
  const updateCategory = useUpdateCategoryMutation();
  const toast = useToast();

  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);

  const onDeletePurpose = useCallback(
    (purposeToDelete: string) => {
      if (!category) {
        return;
      }

      const filteredSecondaryPurposes =
        category.secondaryPurposes?.filter((item) => item !== null && item !== undefined) ?? [];

      const updatedPurposesArray = filteredSecondaryPurposes.filter((purpose) => purpose !== purposeToDelete);

      updateCategory.mutate(
        {
          ...cleanCategoryForMutation(category),
          secondaryPurposes: [...updatedPurposesArray],
        },
        {
          onSuccess: (_, variables) => {
            refetchCategoryAndBlocks();
            onUpdateCategory && onUpdateCategory(variables);
            toast({
              title: t('toasts.purposeDeleted'),
              status: 'success',
              duration: 3000,
              isClosable: true,
            });
          },
          onError: (error) => {
            toast({
              title: error?.response?.errors?.[0]?.message || t('messages.error'),
              status: 'error',
              duration: 3000,
              isClosable: true,
            });
          },
        },
      );
    },
    [category, updateCategory, toast, onUpdateCategory, t],
  );

  useEffect(() => {
    if (showDeleteConfirmation && !isMenuOpen) {
      setShowDeleteConfirmation(false);
    }
  }, [isMenuOpen, showDeleteConfirmation, setShowDeleteConfirmation]);

  return (
    <Flex
      position="relative"
      alignItems="center"
      width={!isUltimate ? 'calc(100% - 150px)' : '100%'}
      padding={`${rem(10)} ${rem(16)}`}
      _hover={{
        backgroundColor: 'background-secondary',
        '.deleteMessage': {
          backgroundColor: 'background-secondary',
        },
      }}
      _focusVisible={{
        backgroundColor: 'background-secondary',
        '.deleteMessage': {
          backgroundColor: 'background-secondary',
        },
      }}
      cursor="pointer"
      onClick={onSelect}
    >
      <Text as="span" textStyle="medium" _hover={{ cursor: 'pointer' }} noOfLines={1}>
        {text}
      </Text>
      {isUltimate && (
        <Text
          as="span"
          textStyle="caption"
          marginLeft={rem(8)}
          color="background-primary"
          borderRadius={rem(4)}
          backgroundColor={getColorFromToken(category?.color)}
          paddingX={rem(8)}
          paddingY={rem(4)}
        >
          {t('messages.ultimate')}
        </Text>
      )}

      {!isUltimate && (
        <Flex
          className="deleteMessage"
          position="absolute"
          top={0}
          right={rem(-150)}
          bottom={0}
          alignItems="center"
          justifyContent="flex-end"
          gap={rem(8)}
          width={rem(150)}
          onClick={(e) => e.stopPropagation()}
          onMouseEnter={() => onButtonInteraction(true)}
          onMouseLeave={() => onButtonInteraction(false)}
        >
          {showDeleteConfirmation ? (
            <>
              {!updateCategory.isLoading && (
                <>
                  <Text as="span" textStyle="small" color="text-primary">
                    {t('messages.areYouSure')}
                  </Text>
                  <IconButton
                    minWidth={rem(14)}
                    height="100%"
                    aria-label={t('ariaLabels.no')}
                    icon={<IconClose height={rem(14)} width={rem(14)} />}
                    isDisabled={updateCategory.isLoading}
                    isLoading={updateCategory.isLoading}
                    onClick={() => {
                      setShowDeleteConfirmation(false);
                      onButtonInteraction(false);
                    }}
                    variant="tertiary"
                  />
                </>
              )}
              <IconButton
                minWidth={rem(14)}
                height="100%"
                marginRight={rem(22)}
                aria-label={t('ariaLabels.yes')}
                icon={<IconCheckmarkSingle height={rem(14)} width={rem(14)} />}
                isDisabled={updateCategory.isLoading}
                isLoading={updateCategory.isLoading}
                onClick={() => {
                  onDeletePurpose(text);
                  onButtonInteraction(false);
                }}
                variant="tertiary"
              />
            </>
          ) : (
            <IconButton
              marginRight={rem(8)}
              aria-label={t('ariaLabels.delete')}
              icon={<IconTrash />}
              isDisabled={updateCategory.isLoading}
              isLoading={updateCategory.isLoading}
              onClick={() => {
                setShowDeleteConfirmation(true);
                onButtonInteraction(true);
              }}
              variant="tertiary"
            />
          )}
        </Flex>
      )}
    </Flex>
  );
};

const PurposeSelect = forwardRef<HTMLTextAreaElement, PurposeSelectProps>(
  (
    {
      behavior = PURPOSE_SELECT_BEHAVIOR_BLOCK,
      category,
      hideLabel = false,
      label = 'Purpose',
      value,
      onChange,
      onUpdateCategory,
      ...props
    },
    ref,
  ) => {
    const { t } = useTranslation('PurposeSelect');
    const [isMenuOpen, setIsMenuOpen] = useState(false);
    const [isInteractingWithButtons, setIsInteractingWithButtons] = useState(false);
    const toast = useToast();
    const containerRef = useRef<HTMLDivElement>(null);

    const updateCategory = useUpdateCategoryMutation();

    // Close dropdown when clicking outside
    useEffect(() => {
      const handleClickOutside = (event: MouseEvent) => {
        if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
          setIsMenuOpen(false);
          setIsInteractingWithButtons(false);
        }
      };

      if (isMenuOpen) {
        document.addEventListener('mousedown', handleClickOutside);
        return () => {
          document.removeEventListener('mousedown', handleClickOutside);
        };
      }
    }, [isMenuOpen]);

    const purposes: PurposeSelectItem[] = useMemo(() => {
      const purposes: PurposeSelectItem[] = [];
      if (category?.ultimatePurpose) {
        purposes.push({ text: category?.ultimatePurpose, isUltimate: true, id: 1 });
      }
      return purposes
        .concat(
          category?.secondaryPurposes?.map((purpose, index) => ({
            id: index + 2,
            text: purpose,
          })) || [],
        )
        .filter((purpose) => purpose.text !== '' && purpose.text !== undefined);
    }, [category]);

    const setUltimatePurposeForCategory = useCallback(
      (purpose: string) => {
        if (!category) {
          return;
        }
        const filteredSecondaryPurposes =
          category?.secondaryPurposes?.filter((item) => item !== null && item !== undefined) ?? [];

        // if empty string => delete current ultimate purpose from the category
        // if new value => make current ultimate purpose secondary, create new ultimate purpose for the category
        // if one of the values in the list => make the current ultimate purpose secondary, and selected value the new ultimate
        let nextCategory = null;
        const previousUltimatePurpose = category.ultimatePurpose;
        if (purpose.trim() === '') {
          nextCategory = {
            ...cleanCategoryForMutation(category),
            ultimatePurpose: '',
          };
        } else if (!purposes.find((p) => p.text.trim().toLowerCase() === purpose.trim().toLowerCase())) {
          nextCategory = {
            ...cleanCategoryForMutation(category),
            ultimatePurpose: purpose,
          };
          if (previousUltimatePurpose) {
            nextCategory.secondaryPurposes = [
              ...filteredSecondaryPurposes.filter((p) => p !== previousUltimatePurpose),
              previousUltimatePurpose,
            ];
          }
        } else if (purpose !== category.ultimatePurpose) {
          nextCategory = {
            ...cleanCategoryForMutation(category),
            ultimatePurpose: purpose,
          };
          nextCategory.secondaryPurposes = [...filteredSecondaryPurposes.filter((p) => p !== purpose)];
          if (previousUltimatePurpose) {
            nextCategory.secondaryPurposes.push(previousUltimatePurpose);
          }
        }
        if (nextCategory) {
          updateCategory.mutate(nextCategory, {
            onSuccess: async (_, variables) => {
              refetchCategoryAndBlocks();
              onUpdateCategory && onUpdateCategory(variables);
              toast({
                title: t('toasts.purposeUpdated'),
                status: 'success',
                duration: 3000,
                isClosable: true,
              });
            },
          });
        }
      },
      [category, purposes, updateCategory, onUpdateCategory, toast, t],
    );

    const createPurpose = useCallback(
      (purpose: string) => {
        if (behavior === PURPOSE_SELECT_BEHAVIOR_CATEGORY) {
          return setUltimatePurposeForCategory(purpose);
        }

        if (
          purpose.trim() !== '' &&
          !purposes.find((p) => p.text.trim().toLowerCase() === purpose.trim().toLowerCase()) &&
          category
        ) {
          const filteredSecondaryPurposes =
            category?.secondaryPurposes?.filter((item) => item !== null && item !== undefined) ?? [];

          updateCategory.mutate(
            {
              ...cleanCategoryForMutation(category),
              secondaryPurposes: [...filteredSecondaryPurposes, purpose],
            },
            {
              onSuccess: async (_, variables) => {
                refetchCategoryAndBlocks();
                onUpdateCategory && onUpdateCategory(variables);
                toast({
                  title: t('toasts.purposeCreated'),
                  status: 'success',
                  duration: 3000,
                  isClosable: true,
                });
              },
            },
          );
        }
      },
      [behavior, category, purposes, setUltimatePurposeForCategory, updateCategory, onUpdateCategory, toast, t],
    );

    return (
      <Box ref={containerRef} position="relative">
        {!hideLabel && (
          <Text as="label" textStyle="caption" color="text-tertiary">
            {t('labels.purpose')}
          </Text>
        )}
        <Box marginTop={rem(16)}>
          <Textarea
            ref={ref}
            as={AutoResizeTextarea}
            sx={{
              height: 'auto',
              display: 'block',
              whiteSpace: 'pre-wrap',
              wordBreak: 'break-word',
            }}
            overflow="hidden"
            width="100%"
            minHeight={rem(20)}
            paddingTop={rem(0)}
            paddingRight={rem(0)}
            paddingBottom={rem(16)}
            paddingLeft={rem(0)}
            color={props.color}
            fontSize={props.fontSize || rem(17)}
            fontWeight={500}
            lineHeight="1.3"
            opacity={props.opacity}
            border="none"
            borderBottomWidth="1px"
            borderBottomStyle={props.borderBottomStyle}
            borderBottomColor="stroke-primary"
            borderRadius={0}
            _focusVisible={{
              borderButtom: '1px solid',
              borderColor: 'cyan.400',
              boxShadow: 'none',
            }}
            _placeholder={{
              color: 'text-tertiary',
              opacity: 0.5,
              fontSize: props.fontSize || rem(17),
              fontWeight: 500,
            }}
            resize="none"
            isDisabled={props.isDisabled}
            onBlur={(e: React.FocusEvent<HTMLTextAreaElement>) => {
              // Don't close if user is interacting with delete buttons
              if (isInteractingWithButtons) {
                return;
              }
              // Delay closing to allow clicking on dropdown items
              setTimeout(() => {
                if (!isInteractingWithButtons) {
                  setIsMenuOpen(false);
                }
              }, 150);
              if (!props?.error) createPurpose(e.target.value);
            }}
            onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => {
              onChange(e.target.value);
              // Close dropdown when user starts typing
              if (isMenuOpen) {
                setIsMenuOpen(false);
              }
            }}
            onFocus={() => setIsMenuOpen(true)}
            onInput={(e: React.FormEvent<HTMLTextAreaElement>) => {
              const el = e.currentTarget;
              el.style.height = 'auto';
              el.style.height = `${el.scrollHeight}px`;
            }}
            onKeyDown={(e: React.KeyboardEvent<HTMLTextAreaElement>) => {
              if (e.key === 'Enter' && !e.shiftKey && !props?.error) {
                e.preventDefault();
                createPurpose(e.currentTarget.value);
              }
              if (e.key === 'Escape') setIsMenuOpen(false);
            }}
            placeholder={props.placeholder}
            value={value}
          />
        </Box>

        {/* Custom dropdown */}
        {isMenuOpen && purposes.length > 0 && (
          <Box
            position="absolute"
            zIndex={1000}
            top="calc(100% + 10px)"
            right={0}
            left={0}
            width="100%"
            border={`${rem(1)} solid`}
            borderColor="stroke-primary"
            borderRadius={`0 0 ${rem(4)} ${rem(4)}`}
            boxShadow="0px 4px 12px rgba(0, 0, 0, 0.15)"
            backgroundColor="background-primary"
          >
            <VStack
              alignItems="stretch"
              gap={0}
              overflowY="auto"
              width="100%"
              maxHeight={rem(200)}
              paddingBottom={rem(8)}
            >
              <Text textStyle="xsmall" padding={`${rem(16)} ${rem(16)} ${rem(8)}`} color="text-tertiary">
                {t('messages.selectOrCreate')}
              </Text>
              {purposes.map((purpose) => (
                <SelectItem
                  key={purpose.text}
                  category={category}
                  onUpdateCategory={onUpdateCategory}
                  isMenuOpen={isMenuOpen}
                  text={purpose.text}
                  isUltimate={purpose.isUltimate}
                  id={purpose.id}
                  onSelect={() => {
                    if (behavior === PURPOSE_SELECT_BEHAVIOR_CATEGORY) {
                      setUltimatePurposeForCategory(purpose.text);
                    } else {
                      onChange(purpose.text);
                    }
                    setIsMenuOpen(false);
                  }}
                  onButtonInteraction={setIsInteractingWithButtons}
                />
              ))}
            </VStack>
          </Box>
        )}
      </Box>
    );
  },
);

export default PurposeSelect;
